import Logger from '../config/logger';

class YTPlayer {
  static init(overlaySelector, frameSelector, actionSelector) {
    return new YTPlayer(overlaySelector, frameSelector, actionSelector);
  }

  constructor(
    overlaySelector = null,
    frameSelector = null,
    actionSelector = null
  ) {
    this.selectors = {
      videoOverlaySelector: overlaySelector || '#video-play-overlay',
      videoFrameSelector: frameSelector || '#video-play-frame',
      videoActionsSelector: actionSelector || '.js-play-video-action',
    };

    this.els = {
      videoOverlay: document.querySelectorAll(
        this.selectors.videoOverlaySelector
      )[0],
      videoFrame: document.querySelectorAll(
        this.selectors.videoFrameSelector
      )[0],
      videoActions: document.querySelectorAll(
        this.selectors.videoActionsSelector
      ),
    };

    this.YTPlayerIsReady = false;
    this.defaultVideoId = 'QIgocFKBjrc';
    this.defaultVideoName = 'All Together Now';
    this.currentVideoId = '';
    this.currentVideoName = '';
    this.player = null;

    this.addEventListeners();
  }

  initPlayer() {
    wait('YT.Player').then(() => {
      this.player = new window.YT.Player(this.els.videoFrame, {
        height: '315',
        width: '560',
        events: {
          onReady: this.onPlayerReady.bind(this),
          onStateChange: this.onPlayerStateChange.bind(this),
        },
      });
    });

    function wait(key) {
      return new Promise((resolve, reject) => {
        let timeout = 3000;

        const check = () => {
          timeout -= 100;
          const path = key.split('.').reduce((acc, curr) => acc[curr], window);

          if (path) {
            resolve();
          } else if (timeout < 0) {
            reject(Error('timed out!'));
          } else {
            setTimeout(check, 100);
          }
        };

        check();
      });
    }
  }

  onPlayerReady() {
    this.YTPlayerIsReady = true;

    this.playVideo();
  }

  onPlayerStateChange({ data }) {
    if (data === 0 || data === 2) {
      const currentTime = !this.player.getCurrentTime
        ? 0.0
        : this.player.getCurrentTime();
      const duration = !this.player.getDuration
        ? 0.0
        : this.player.getDuration();
      const percentPlayed = parseInt((currentTime / duration) * 100, 10);
      const percentPlayedRound = Math.round(percentPlayed / 10) * 10;

      window.GoogleAnalytics.trackEvent(
        'Video',
        'Watched',
        `${percentPlayedRound}%`
      );
    }
  }

  getVideoId(el) {
    const { videoActionsSelector } = this.selectors;

    const { videoId } = el.closest(videoActionsSelector).dataset;

    if (!videoId || !String(videoId)) {
      const error = Error(
        `Data attr "videoId: ${videoId}" is required for YTPlayer. Expected as string but given ${typeof videoId}`
      );

      Logger.error(error);
    }

    return videoId || null;
  }

  getVideoName(el) {
    const { videoActionsSelector } = this.selectors;

    const { videoName } = el.closest(videoActionsSelector).dataset;

    if (!videoName || !String(videoName)) {
      const error = Error(
        `Data attr "videoName: ${videoName}" is required for YTPlayer. Expected as string but given ${typeof videoName}`
      );

      Logger.error(error);
    }

    return videoName || null;
  }

  initOverlay() {
    const { videoOverlay } = this.els;

    if (videoOverlay) {
      videoOverlay.addEventListener('click', () => {
        this.onClose();
      });
    }
  }

  playVideo() {
    const { videoOverlay } = this.els;

    if (videoOverlay) {
      videoOverlay.classList.add('active');
    }

    if (
      this.player &&
      typeof this.player.playVideo === 'function' &&
      typeof this.player.seekTo === 'function' &&
      typeof this.player.loadVideoById === 'function'
    ) {
      this.player.loadVideoById(this.currentVideoId || this.defaultVideoId);
      this.player.seekTo(0);
      this.player.playVideo();

      window.GoogleAnalytics.trackEvent(
        'Video',
        'Play',
        this.currentVideoName || this.defaultVideoName
      );
      window.HotJarHelper.triggerEvent('youtube_video_play');

      this.removeLoading();
    }
  }

  pauseVideo() {
    if (this.player && typeof this.player.pauseVideo === 'function') {
      this.player.pauseVideo();
      window.GoogleAnalytics.trackEvent(
        'Video',
        'Stop',
        this.currentVideoName || this.defaultVideoName
      );
    }
  }

  addLoading(element) {
    if (element && element.setAttribute) {
      element.setAttribute('data-loading', 'true');
    } else {
      const { videoActions } = this.els;

      Array.from(videoActions).forEach((actionEl) => {
        actionEl?.setAttribute('data-loading', 'true');
      });
    }
  }

  removeLoading(element) {
    if (element && element.setAttribute) {
      element.setAttribute('data-loading', 'false');
    } else {
      const { videoActions } = this.els;

      Array.from(videoActions).forEach((actionEl) => {
        actionEl?.setAttribute('data-loading', 'false');
      });
    }
  }

  onClose() {
    const { videoOverlay } = this.els;

    if (videoOverlay) {
      videoOverlay.classList.remove('active');
    }

    this.pauseVideo();
  }

  addEventListeners() {
    const { videoActions } = this.els;

    [].forEach.call(videoActions, (playVideoAction) => {
      playVideoAction.addEventListener('click', () => {
        this.addLoading(playVideoAction);

        this.currentVideoId = this.getVideoId(playVideoAction);
        this.currentVideoName = this.getVideoName(playVideoAction);

        if (this.currentVideoId === null || this.currentVideoName === null) {
          return false;
        }

        if (this.YTPlayerIsReady) {
          this.playVideo();
        } else {
          const runPlayer = () =>
            loadYTPlayerAPI()
              .then(() => {
                if (window.onYouTubeIframeAPIReady === undefined) {
                  window.onYouTubeIframeAPIReady = () => {
                    this.initPlayer();
                  };
                } else {
                  this.initPlayer();
                }
              })
              .catch((err) => {
                setTimeout(() => runPlayer(), 1000);

                console.error(err);
              });

          runPlayer();

          this.initOverlay();
        }

        return false;
      });
    });

    function loadYTPlayerAPI() {
      return new Promise((resolve, reject) => {
        const tag = document.createElement('script');

        tag.onload = () => {
          if (window.YT) {
            resolve();
          } else {
            reject(Error(`window.YT undefined`));
          }
        };
        tag.src = 'https://www.youtube.com/iframe_api';

        const firstScriptTag = document.getElementsByTagName('script')[0];

        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      });
    }
  }
}

export default YTPlayer;
